import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Theme from "@adsk/alloy-react-theme";
import { useTranslations } from "@adsk/acc-issues-translations";
import { DocumentTextIcon, PhotoIcon } from "@adsk/alloy-react-icon";
import Button from "@adsk/alloy-react-button";
import { ChangedTextActivityCard } from "./ChangedTextActivityCard";
import { ActivityVerb, activityVerbs } from "@adsk/acc-issues-types";
import { IssueAttachment } from "@adsk/acc-issues-types";
import {
  getIsIssueAttachmentsLoading,
  getAllIssueAttachments,
} from "@adsk/acc-issues-core/state/redux/ducks/issueAttachments";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { getIssueById } from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import { useSdk } from "@adsk/acc-issues-core/state/sdk/useSdk";
import { PhotoActivityCard } from "@adsk/alloy-react-activity-log";
import { ThumbnailImageDisplay } from "../../IssueDetailTabPanel/detail-panel-fields/ThumbnailField";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import { useSetSnapshotAsAThumbnail } from "../../IssueDetailTabPanel/hooks/IssueSnapshot/useSetSnapshotAsAThumbnail";
import { IssueSnapshotCreateTrigger } from "../../../utils/analytics/analyticsLib093IssuesEvents";
import { useSnapshotAbortController } from "../../IssueDetailTabPanel/hooks/IssueSnapshot/useSnapshotAbortController";
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from "@adsk/alloy-react-notification";
import { useActiveDetailPanelTab } from "../../ActiveDetailPanelTabContext";
import { IssueDetailPanelTabs } from "../../IssueDetailPanelTabType";
import { isAttributeEditable } from "../../../utils/data/entityPermissions";

type AttachmentActivityCardProps = {
  commonActivityProps: any;
  attachment: {
    id: string;
    displayName: string;
    fileName: string;
    fileType: string;
    storageUrn?: string;
  };
  verb: ActivityVerb;
};

const AttachmentImageActivityContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ImageContainer = styled.div`
  max-height: 160px;
`;

const ImageTextContainer = styled.span`
  color: ${Theme.colors.charcoal900};
  font-size: 14px;
`;

const FileTextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SetAsThumbnailButton = styled(Button)`
  position: absolute;
  bottom: 21px;
  right: 23px;
`;

export const AttachmentActivityCard = ({
  commonActivityProps,
  attachment,
  verb,
}: AttachmentActivityCardProps) => {
  const [isSetButtonLoading, setIsSetButtonLoading] = useState(false);
  const { T } = useTranslations();
  const [url, setUrl] = useState("");
  const sdk = useSdk();
  const { id, fileType, storageUrn, displayName } = attachment;
  const issueId = commonActivityProps.activityChange.object.id;
  const getIssue = useSelector(getIssueById);
  const issue = getIssue(issueId);
  const issueAttachments = useSelector(getAllIssueAttachments)(issueId);
  const isLoadingAttachments = useSelector(getIsIssueAttachmentsLoading)(
    issueId
  );
  const setSnapshotAsThumbnail = useSetSnapshotAsAThumbnail();
  const { getFlowId } = useSnapshotAbortController();
  const { showNotification } = useNotifications();
  const [, setIssueDetailPanelTab] = useActiveDetailPanelTab();

  const { value: shouldShowAttachmentActivityImage } = useFeatureFlag(
    FeatureFlag.Attachment1_1ActivityImages
  );

  const shouldRenderSnapshotViewActionButtons = isAttributeEditable(
    issue,
    "snapshotUrn"
  );

  useEffect(() => {
    const fetchUrl = async () => {
      if (storageUrn) {
        const { url } = await sdk.Oss.getDownloadLink(storageUrn);
        setUrl(url);
      }
    };
    fetchUrl();
  }, [sdk.Oss, storageUrn]);

  let action;
  switch (verb) {
    case activityVerbs.ISSUE_ATTACHMENT_ADD:
      action = "added";
      break;
    case activityVerbs.ISSUE_ATTACHMENT_REMOVE:
      action = "removed";
      break;
    default:
      action = "";
  }

  const isAttachmentRemoved = useMemo(() => {
    if (verb === activityVerbs.ISSUE_ATTACHMENT_REMOVE) {
      return true;
    }
    if (isLoadingAttachments) return false;
    return !issueAttachments.some(
      (attachment: IssueAttachment) => attachment.attachmentId === id
    );
  }, [verb, issueAttachments, id, isLoadingAttachments]);

  const isImage = ["png", "jpg", "jpeg"].includes(fileType);

  const Icon = isImage ? PhotoIcon : DocumentTextIcon;

  const displayNonImageFileName = useCallback(() => {
    return (
      <span
        style={{
          textDecoration: isAttachmentRemoved ? "line-through" : "normal",
          fontSize: "14px",
        }}
      >
        {displayName}
      </span>
    );
  }, [displayName, isAttachmentRemoved]);

  const handleSetAttachmentAsThumbnail = useCallback(async () => {
    setIsSetButtonLoading(true);
    try {
      await setSnapshotAsThumbnail({
        issue,
        storageUrn,
        hasMarkups: false,
        trigger: IssueSnapshotCreateTrigger.SET_THUMBNAIL_BUTTON,
        flowId: getFlowId(issue.id),
        handleFailureNotification: () => {
          showNotification({
            status: NOTIFICATION_STATUSES.ERROR,
            message: T.translate("thumbnailField.snapshotView.failureMessage"),
            dismissible: true,
          });
        },
        onSuccess: async () => {
          showNotification({
            status: NOTIFICATION_STATUSES.SUCCESS,
            message: T.translate("thumbnailField.snapshotView.successMessage"),
            dismissible: true,
          });
          setIssueDetailPanelTab(IssueDetailPanelTabs.Details);
        },
      });
    } finally {
      setIsSetButtonLoading(false);
    }
  }, [
    issue,
    setSnapshotAsThumbnail,
    storageUrn,
    getFlowId,
    showNotification,
    T,
    setIssueDetailPanelTab,
  ]);

  const shouldDisableSetThumbnailButton = useMemo(() => {
    return storageUrn === issue.snapshotUrn || isSetButtonLoading;
  }, [storageUrn, issue, isSetButtonLoading]);

  const renderSetAsThumbnailButton = () => {
    return (
      <SetAsThumbnailButton
        variant={Button.VARIANTS.PRIMARY}
        onClick={handleSetAttachmentAsThumbnail}
        loading={isSetButtonLoading}
        disabled={shouldDisableSetThumbnailButton}
        data-testid="set-as-thumbnail-button"
      >
        {T.translate("thumbnailField.snapshotView.restoreThumbnail")}
      </SetAsThumbnailButton>
    );
  };

  return shouldShowAttachmentActivityImage &&
    isImage &&
    url &&
    !isAttachmentRemoved ? (
    <PhotoActivityCard
      className="IssuesActivityLog__activity-card"
      {...commonActivityProps}
      title={T.translate(`activityLog.titles.issueAttachment.${action}`)}
    >
      <AttachmentImageActivityContainer data-testid="activity-attachment-image-container">
        <ImageTextContainer>{displayName}</ImageTextContainer>
        <ImageContainer>
          <ThumbnailImageDisplay
            url={url}
            issue={issue}
            renderViewHeader={() => <div>{displayName}</div>}
            renderSnapshotViewActionButtons={
              shouldRenderSnapshotViewActionButtons
                ? renderSetAsThumbnailButton
                : undefined
            }
          />
        </ImageContainer>
      </AttachmentImageActivityContainer>
    </PhotoActivityCard>
  ) : (
    <ChangedTextActivityCard
      {...commonActivityProps}
      title={T.translate(`activityLog.titles.issueAttachment.${action}`)}
      text={
        <FileTextContainer>
          <Icon />
          {displayNonImageFileName()}
        </FileTextContainer>
      }
    />
  );
};
